import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { errorMessage } from '@/lib/errors';
import { useCreateFinanceRule, useUpdateFinanceRule } from '@/lib/finance';
import { CURRENCIES, fromMinor, toMinor } from '@/lib/money';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceCounterparty,
  FinanceRule,
  FinanceRuleParams,
  FinanceRuleUpdate,
  FinanceRuleWrite,
} from '@/api/generated/types.gen';

type RuleType = FinanceRule['type'];
type RuleScope = FinanceRule['scope'];
type Attribution = FinanceRule['attribution'];
type ScheduleKind = 'monthly' | 'twice_monthly' | 'weekly' | 'biweekly';

const TYPE_TABS: TabItem[] = [
  { value: 'recurring', label: 'Recurring' },
  { value: 'per_stay', label: 'Per stay' },
  { value: 'commission_invoice', label: 'Commission invoice' },
];

const WEEKDAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

export interface RuleFormRefs {
  categories: readonly FinanceCategory[];
  apartments: readonly { id: string; title: string }[];
  counterparties: readonly FinanceCounterparty[];
  accounts: readonly FinanceAccount[];
  /**
   * Every per_stay rule with no parent of its own — the only legal parent
   * (ValidateRuleParams, internal/domain/finance/rules.go: parent must be
   * per_stay and must not itself have a parent). Pass the UNFILTERED set —
   * this component narrows to the chosen apartment itself (ValidateRuleParams:
   * "dependent rule must share the parent's apartment"), so a caller does not
   * need a second query every time the apartment picker changes. Include
   * disabled rules: a dependent already pointed at a since-paused parent must
   * still resolve its name and currency when the operator opens it to edit.
   */
  parentCandidates: readonly FinanceRule[];
  /** finance_settings.import_cutoff (YYYY-MM-DD). Omitted skips the
   * client-side start_date check — the server still enforces it either way. */
  importCutoff?: string;
}

export interface RuleFormProps {
  refs: RuleFormRefs;
  /** Edit an existing rule instead of creating one. type/scope/apartment_id/
   * category_id/parent_rule_id/start_date are immutable once a rule exists
   * (FinanceRuleUpdate's own doc comment) — shown for context, disabled. */
  initial?: FinanceRule;
  onDone?: (rule: FinanceRule) => void;
  onCancel?: () => void;
}

function schemaWeekdayOf(dateStr: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const jsDay = d.getUTCDay(); // 0=Sunday..6=Saturday
  return jsDay === 0 ? 7 : jsDay; // schema: 1=Monday..7=Sunday
}

/**
 * Three-tab rule editor (spec §14, §7.4's ValidateRuleParams mirrored
 * client-side so a mistake surfaces as a problems-list item here rather than
 * a 422 after a round trip). Used both for "New rule" (finance.rules.tsx, in
 * a side Dialog) and for editing (finance.rules.$id.tsx, as the page body) —
 * the two differ only in whether `initial` is set, not in which component
 * renders them.
 *
 * Validation lives in a live `problems` array recomputed every render, same
 * as SubmissionForm/SettleDialog in this package — not NewBookingForm's
 * throw-inside-mutationFn (components/booking/NewBookingForm.tsx): this form
 * calls the shared useCreateFinanceRule/useUpdateFinanceRule mutations (the
 * useFinanceMutation wrapper every other finance write goes through, for the
 * one invalidation-by-prefix all finance pages rely on), and disabling Save
 * while problems.length > 0 gives the operator the same live feedback a
 * throw-and-catch would, without a second, parallel validation idiom in this
 * package.
 */
export function RuleForm({ refs, initial, onDone, onCancel }: RuleFormProps) {
  const locked = !!initial;
  // Switchable only pre-save: once `initial` is set, `effectiveType` always
  // reads initial.type (type cannot be patched, FinanceRuleUpdate's own doc
  // comment) regardless of what liveType holds — the Tabs strip that would
  // call setLiveType is not even rendered in edit mode below.
  const [liveType, setLiveType] = useState<RuleType>(initial?.type ?? 'recurring');
  const effectiveType = initial?.type ?? liveType;

  const [name, setName] = useState(initial?.name ?? '');
  const [rawScope, setRawScope] = useState<RuleScope>(initial?.scope ?? 'company');
  const [apartmentId, setApartmentId] = useState(initial?.apartment_id ?? '');
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? '');
  const [counterpartyId, setCounterpartyId] = useState(initial?.counterparty_id ?? '');
  const [rawAmount, setRawAmount] = useState(() =>
    initial ? fromMinor(initial.amount_minor, initial.currency) : '',
  );
  const [rawCurrency, setRawCurrency] = useState(initial?.currency ?? 'RSD');
  const [defaultAccountId, setDefaultAccountId] = useState(initial?.default_account_id ?? '');
  const [vatDefault, setVatDefault] = useState(initial?.vat_default ?? false);
  const [expenseTypeDefault, setExpenseTypeDefault] = useState<
    '' | 'operational' | 'capital' | 'financing'
  >(initial?.expense_type_default ?? '');
  const [rawAttribution, setRawAttribution] = useState<Attribution>(initial?.attribution ?? 'same_month');
  const [startDate, setStartDate] = useState(initial?.start_date ?? '');
  const [endDate, setEndDate] = useState(initial?.end_date ?? '');

  // recurring params
  const initialSchedule =
    initial?.type === 'recurring' ? (initial.params as { schedule?: Record<string, unknown> }).schedule : undefined;
  const [scheduleKind, setScheduleKind] = useState<ScheduleKind>(
    (initialSchedule?.kind as ScheduleKind) ?? 'monthly',
  );
  const initialDays = (initialSchedule?.days as number[] | undefined) ?? [];
  const [day1, setDay1] = useState(initialDays[0] != null ? String(initialDays[0]) : '');
  const [day2, setDay2] = useState(initialDays[1] != null ? String(initialDays[1]) : '');
  const [weekday, setWeekday] = useState(
    initialSchedule?.weekday != null ? String(initialSchedule.weekday) : '1',
  );
  const [anchor, setAnchor] = useState((initialSchedule?.anchor as string | undefined) ?? '');

  // per_stay params
  const initialPerStay = initial?.type === 'per_stay' ? (initial.params as { trigger?: string }) : undefined;
  const [trigger, setTrigger] = useState<'checkin' | 'checkout'>(
    (initialPerStay?.trigger as 'checkin' | 'checkout' | undefined) ?? 'checkin',
  );
  const [rawParentRuleId, setRawParentRuleId] = useState(initial?.parent_rule_id ?? '');
  const [percentPctText, setPercentPctText] = useState(() =>
    initial?.percent_bp != null ? String(initial.percent_bp / 100) : '',
  );

  // commission_invoice params
  const initialCommission =
    initial?.type === 'commission_invoice'
      ? (initial.params as { vat_multiplier_bp?: number; due_day?: number })
      : undefined;
  const [vatMultiplierBPText, setVatMultiplierBPText] = useState(
    initialCommission?.vat_multiplier_bp != null ? String(initialCommission.vat_multiplier_bp) : '12000',
  );
  const [dueDayText, setDueDayText] = useState(
    initialCommission?.due_day != null ? String(initialCommission.due_day) : '5',
  );

  const [failure, setFailure] = useState<string | null>(null);

  const create = useCreateFinanceRule();
  const update = useUpdateFinanceRule();
  const busy = create.isPending || update.isPending;

  // Forced, not synced: a type that dictates its scope/attribution simply
  // ignores the raw picker state rather than resetting it — switching tabs
  // and back loses nothing the operator typed, and there is no effect racing
  // a render to keep two copies of the same fact in sync (rulings.md's
  // "adjust state during render" pattern, same shape useOperationId uses).
  const forcedScope: RuleScope | null =
    effectiveType === 'per_stay' ? 'apartment' : effectiveType === 'commission_invoice' ? 'company' : null;
  const scope = forcedScope ?? rawScope;
  const forcedAttribution: Attribution | null = effectiveType === 'commission_invoice' ? 'same_month' : null;
  const attribution = forcedAttribution ?? rawAttribution;

  const effectiveParentRuleId = locked ? (initial?.parent_rule_id ?? '') : rawParentRuleId;
  const hasParent = effectiveType === 'per_stay' && effectiveParentRuleId !== '';
  const parent = refs.parentCandidates.find((r) => r.id === effectiveParentRuleId);
  // A dependent's currency must match its parent's — ValidateRuleParams
  // refuses anything else outright ("converting the planned amount is not
  // implemented this slice"). Locking the picker to the parent's own
  // currency, rather than merely validating it after the fact, means the
  // operator is never shown a choice the server would reject.
  const currency = hasParent ? (parent?.currency ?? rawCurrency) : rawCurrency;
  // The converger computes a dependent's PLANNED amount as percent_bp of the
  // PARENT's own live amount (stay.go) — this rule's own amount_minor plays
  // no part once a parent is chosen, so asking for one would be a field the
  // operator fills in for nothing. Hidden, and sent as 0.
  const hideAmount = hasParent;
  const amountMinor = hideAmount ? 0 : toMinor(rawAmount, currency);

  const parentOptions = refs.parentCandidates.filter(
    (r) => r.type === 'per_stay' && !r.parent_rule_id && r.apartment_id === apartmentId,
  );

  const category = refs.categories.find((c) => c.id === categoryId);

  // Computed once, ahead of the type-branch below, so both the validation
  // push (per_stay branch) and submit()'s body-building read the exact same
  // value — no second parse-and-round anywhere else in this component.
  const percentPctParsed = parseFloat(percentPctText);
  const percentBP: number | null =
    hasParent && Number.isFinite(percentPctParsed) ? Math.round(percentPctParsed * 100) : null;

  // ---- live validation (mirrors ValidateRuleParams, rules.go) ----
  const problems: string[] = [];
  if (!name.trim()) problems.push('Enter a name.');
  if (scope === 'apartment' && !apartmentId) problems.push('Choose an apartment.');
  if (!categoryId) problems.push('Choose an article.');
  if (category?.requires_counterparty && !counterpartyId) {
    problems.push('This article requires a counterparty.');
  }
  if (!hideAmount && (amountMinor === null || amountMinor < 0)) {
    problems.push('Enter a valid amount (0 or more).');
  }
  if (!locked && !startDate) problems.push('Choose a start date.');
  if (!locked && refs.importCutoff && startDate && startDate < refs.importCutoff) {
    problems.push(`Start date must be on or after ${refs.importCutoff} (the import cutoff).`);
  }
  if (endDate && startDate && endDate < startDate) problems.push('End date cannot be before start date.');
  if (effectiveType === 'per_stay' && hasParent) {
    if (percentBP === null || percentBP <= 0 || percentBP > 10000) {
      problems.push("Percent of parent's amount must be above 0% and at most 100%.");
    }
  }

  let typeParams: FinanceRuleParams = {};
  if (effectiveType === 'recurring') {
    const days: number[] = [];
    if (scheduleKind === 'monthly') {
      const d = Number(day1);
      if (!Number.isInteger(d) || d < 1 || d > 31) problems.push('Day must be 1–31.');
      else days.push(d);
    } else if (scheduleKind === 'twice_monthly') {
      const d1 = Number(day1);
      const d2 = Number(day2);
      if (![d1, d2].every((d) => Number.isInteger(d) && d >= 1 && d <= 31)) {
        problems.push('Both days must be 1–31.');
      } else if (d1 === d2) {
        problems.push('The two days must be different.');
      } else {
        days.push(d1, d2);
      }
    }
    const weekdayNum = Number(weekday);
    const needsWeekday = scheduleKind === 'weekly' || scheduleKind === 'biweekly';
    if (needsWeekday && (!Number.isInteger(weekdayNum) || weekdayNum < 1 || weekdayNum > 7)) {
      problems.push('Choose a weekday.');
    }
    let anchorOut: string | undefined;
    if (scheduleKind === 'biweekly') {
      if (!anchor) {
        problems.push('Biweekly schedules need an anchor date.');
      } else {
        const wd = schemaWeekdayOf(anchor);
        if (wd === null) problems.push('Anchor date is invalid.');
        else if (wd !== weekdayNum) {
          // Same wording as ValidateRuleParams' own rejection (rules.go), so
          // an operator who somehow still hits the 422 recognises it as the
          // same rule, not a second, different-sounding error.
          problems.push(`biweekly anchor ${anchor} does not fall on the chosen weekday`);
        } else {
          anchorOut = anchor;
        }
      }
    }
    typeParams = {
      schedule: {
        kind: scheduleKind,
        ...(days.length > 0 ? { days } : {}),
        ...(needsWeekday ? { weekday: weekdayNum } : {}),
        ...(anchorOut ? { anchor: anchorOut } : {}),
      },
    };
  } else if (effectiveType === 'per_stay') {
    typeParams = { trigger };
    if (scope !== 'apartment') problems.push('per_stay rules are per apartment.');
  } else {
    const vatMultiplier = Number(vatMultiplierBPText);
    if (!Number.isInteger(vatMultiplier) || vatMultiplier < 10000 || vatMultiplier > 20000) {
      problems.push('VAT multiplier must be 10000–20000 (12000 = ×1.2, i.e. 20% VAT).');
    }
    const dueDay = Number(dueDayText);
    if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 28) {
      problems.push('Due day must be 1–28.');
    }
    typeParams = { channel: 'booking_com', vat_multiplier_bp: vatMultiplier, due_day: dueDay };
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setFailure(null);
    if (problems.length > 0) return;
    const params = typeParams;
    if (locked && initial) {
      const body: FinanceRuleUpdate = {
        name: name.trim(),
        counterparty_id: counterpartyId || undefined,
        amount_minor: hideAmount ? 0 : (amountMinor as number),
        currency,
        default_account_id: defaultAccountId || undefined,
        vat_default: vatDefault,
        expense_type_default: expenseTypeDefault || undefined,
        attribution,
        params,
        percent_bp: hasParent ? (percentBP as number) : undefined,
        end_date: endDate || undefined,
      };
      update.mutate(
        { id: initial.id, body },
        { onSuccess: (rule) => onDone?.(rule), onError: (err) => setFailure(errorMessage(err)) },
      );
      return;
    }
    const body: FinanceRuleWrite = {
      type: effectiveType,
      name: name.trim(),
      scope,
      apartment_id: scope === 'apartment' ? apartmentId : undefined,
      category_id: categoryId,
      counterparty_id: counterpartyId || undefined,
      amount_minor: hideAmount ? 0 : (amountMinor as number),
      currency,
      default_account_id: defaultAccountId || undefined,
      vat_default: vatDefault,
      expense_type_default: expenseTypeDefault || undefined,
      attribution,
      params,
      parent_rule_id: hasParent ? effectiveParentRuleId : undefined,
      percent_bp: hasParent ? (percentBP as number) : undefined,
      start_date: startDate,
      end_date: endDate || undefined,
    };
    create.mutate(body, {
      onSuccess: (rule) => onDone?.(rule),
      onError: (err) => setFailure(errorMessage(err)),
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {locked ? (
        <p className="text-sm text-muted">
          Type: <span className="font-semibold text-foreground">{TYPE_TABS.find((t) => t.value === effectiveType)?.label}</span>{' '}
          — fixed once created.
        </p>
      ) : (
        <Tabs tabs={TYPE_TABS} value={liveType} onChange={(v) => setLiveType(v as RuleType)} />
      )}

      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Scope"
          value={scope}
          disabled={locked || forcedScope !== null}
          options={[
            { value: 'company', label: 'Company' },
            { value: 'apartment', label: 'Apartment' },
          ]}
          onChange={(e) => setRawScope(e.target.value as RuleScope)}
        />
        {scope === 'apartment' && (
          <Select
            label="Apartment"
            value={apartmentId}
            disabled={locked}
            placeholder="Select apartment…"
            options={refs.apartments.map((a) => ({ value: a.id, label: a.title }))}
            onChange={(e) => setApartmentId(e.target.value)}
          />
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Article"
          value={categoryId}
          disabled={locked}
          placeholder="Select article…"
          options={refs.categories.filter((c) => c.is_active).map((c) => ({ value: c.id, label: c.name }))}
          onChange={(e) => setCategoryId(e.target.value)}
        />
        <Select
          label="Counterparty"
          value={counterpartyId}
          required={category?.requires_counterparty ?? false}
          disabled={refs.counterparties.length === 0}
          // A real '' option, not `placeholder` (Select renders that as
          // `<option disabled>`, permanently unselectable once the operator
          // has picked anything else — this field must stay clearable).
          options={
            refs.counterparties.length === 0
              ? [{ value: '', label: 'None recorded yet' }]
              : [
                  { value: '', label: 'None' },
                  ...refs.counterparties.filter((c) => c.is_active).map((c) => ({ value: c.id, label: c.name })),
                ]
          }
          onChange={(e) => setCounterpartyId(e.target.value)}
        />
      </div>

      {refs.counterparties.length === 0 &&
        // The hint must not promise "save without one" when the chosen article
        // has already made a counterparty mandatory (problems.push above,
        // same category.requires_counterparty check) — that combination
        // permanently disables Create/Save with no explanation of why, or of
        // where to fix it (a counterparty can only be created from Finance
        // settings, a screen this form never links to on its own).
        (category?.requires_counterparty ? (
          <p className="text-xs text-[var(--ollio-gold-deep)]">
            This article requires a counterparty, and none are recorded yet — add one in{' '}
            <Link to="/settings/finance" className="underline">
              Finance settings
            </Link>{' '}
            {/* The link only opens the page — its Counterparties tab is not
                URL-addressable (settings.finance.tsx keeps tab choice in local
                state), so the tab name stays plain text, not part of the
                underline, to avoid promising a jump the click can't do. */}
            → Counterparties tab, then come back and select it here.
          </p>
        ) : (
          <p className="text-xs text-muted">
            You have not recorded any counterparties yet — this rule can be saved without one.
          </p>
        ))}

      <div className="grid gap-4 sm:grid-cols-2">
        {!hideAmount && (
          <MoneyInput label="Amount" currency={currency || 'RSD'} value={rawAmount} onChange={setRawAmount} />
        )}
        <div>
          <Select
            label="Currency"
            value={currency}
            disabled={hasParent}
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
            onChange={(e) => setRawCurrency(e.target.value)}
          />
          {hasParent && (
            <p className="mt-1 text-xs text-muted">
              Locked to the parent rule&rsquo;s currency — converting the planned amount is not implemented
              yet.
            </p>
          )}
        </div>
      </div>
      {hideAmount && (
        <p className="text-xs text-muted">
          This rule's amount is a percentage of its parent's own amount — set the percentage below.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Default account"
          value={defaultAccountId}
          options={[
            { value: '', label: 'None' },
            ...refs.accounts.filter((a) => a.is_active).map((a) => ({ value: a.id, label: a.name })),
          ]}
          onChange={(e) => setDefaultAccountId(e.target.value)}
        />
        <Select
          label="Expense type"
          value={expenseTypeDefault}
          options={[
            { value: '', label: 'None' },
            { value: 'operational', label: 'Operational' },
            { value: 'capital', label: 'Capital' },
            { value: 'financing', label: 'Financing' },
          ]}
          onChange={(e) => setExpenseTypeDefault(e.target.value as typeof expenseTypeDefault)}
        />
      </div>

      <Checkbox label="VAT by default" checked={vatDefault} onChange={(e) => setVatDefault(e.target.checked)} />

      {effectiveType !== 'commission_invoice' ? (
        <Select
          label="Attribution"
          value={attribution}
          options={[
            { value: 'same_month', label: 'Same month' },
            { value: 'previous_month', label: 'Previous month' },
          ]}
          onChange={(e) => setRawAttribution(e.target.value as Attribution)}
        />
      ) : (
        <p className="text-xs text-muted">
          Attribution: same month — the invoice's period_month is computed explicitly from the invoice date
          (spec §7.3), not derived from due_date the way recurring/per_stay rows are.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Start date" type="date" value={startDate} disabled={locked} onChange={(e) => setStartDate(e.target.value)} />
        <Input label="End date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {effectiveType === 'recurring' && (
        <fieldset className="flex flex-col gap-3 border-0 p-0 m-0">
          <legend className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Schedule</legend>
          <Select
            label="Schedule kind"
            value={scheduleKind}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'twice_monthly', label: 'Twice a month' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'biweekly', label: 'Every two weeks' },
            ]}
            onChange={(e) => setScheduleKind(e.target.value as ScheduleKind)}
          />
          {scheduleKind === 'monthly' && (
            <Input label="Day of month" type="number" min={1} max={31} value={day1} onChange={(e) => setDay1(e.target.value)} />
          )}
          {scheduleKind === 'twice_monthly' && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="First day" type="number" min={1} max={31} value={day1} onChange={(e) => setDay1(e.target.value)} />
              <Input label="Second day" type="number" min={1} max={31} value={day2} onChange={(e) => setDay2(e.target.value)} />
            </div>
          )}
          {(scheduleKind === 'weekly' || scheduleKind === 'biweekly') && (
            <Select label="Weekday" value={weekday} options={WEEKDAYS} onChange={(e) => setWeekday(e.target.value)} />
          )}
          {scheduleKind === 'biweekly' && (
            <Input
              label="Anchor date"
              type="date"
              value={anchor}
              onChange={(e) => setAnchor(e.target.value)}
              hint="A date on the chosen weekday — the cadence counts every 14 days from it."
            />
          )}
        </fieldset>
      )}

      {effectiveType === 'per_stay' && (
        <fieldset className="flex flex-col gap-3 border-0 p-0 m-0">
          <legend className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Per-stay</legend>
          <Select
            label="Trigger"
            value={trigger}
            options={[
              { value: 'checkin', label: 'Check-in' },
              { value: 'checkout', label: 'Check-out' },
            ]}
            onChange={(e) => setTrigger(e.target.value as 'checkin' | 'checkout')}
          />
          <Select
            label="Parent rule"
            value={effectiveParentRuleId}
            disabled={locked}
            options={
              parentOptions.length === 0
                ? [{ value: '', label: 'No eligible parent rules for this apartment' }]
                : [
                    { value: '', label: 'None (independent rule)' },
                    ...parentOptions.map((r) => ({ value: r.id, label: r.name })),
                  ]
            }
            onChange={(e) => setRawParentRuleId(e.target.value)}
          />
          {hasParent && (
            <Input
              label="Percent of parent's amount (%)"
              type="number"
              min={0.01}
              max={100}
              step={0.01}
              value={percentPctText}
              onChange={(e) => setPercentPctText(e.target.value)}
            />
          )}
        </fieldset>
      )}

      {effectiveType === 'commission_invoice' && (
        <fieldset className="flex flex-col gap-3 border-0 p-0 m-0">
          <legend className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Commission invoice
          </legend>
          <p className="text-sm text-muted">
            Channel: Booking.com (fixed — Airbnb commission is derived directly from bookings, not invoiced).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="VAT multiplier (bp)"
              type="number"
              min={10000}
              max={20000}
              value={vatMultiplierBPText}
              onChange={(e) => setVatMultiplierBPText(e.target.value)}
              hint="12000 = ×1.2 (20% VAT)."
            />
            <Input
              label="Due day"
              type="number"
              min={1}
              max={28}
              value={dueDayText}
              onChange={(e) => setDueDayText(e.target.value)}
              hint="Day of the month after the invoice month."
            />
          </div>
        </fieldset>
      )}

      {problems.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-sm text-[var(--ollio-gold-deep)]">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={problems.length > 0} loading={busy}>
          {locked ? 'Save' : 'Create rule'}
        </Button>
      </div>
    </form>
  );
}
